/**
 * NotesPanel — the Workspace tab's Notes side panel (plan §11 tab 4, §5.6,
 * §10.6, R33, §13.8 WP-70). Not wired into the Workspace tab's shell here
 * (that slot is WP-55's — this component is the panel content only, taking
 * its selection and negotiation from props).
 *
 * Lists the notes for the selected cell (matched by cell_id when known,
 * else by budget_line_id, else by the sheet+cell provenance pair), the
 * negotiation's other notes collapsed underneath, an add-note form that
 * posts with the cell reference, and a separate outcome-note form for the
 * negotiation (plan §10.6: "the outcome note ... is required before a
 * negotiation is marked executed").
 *
 * One `listNotes({ negotiationId, versionId })` call per negotiation/version
 * change; which notes count as "for the selected cell" is then derived
 * client-side from `selection`, so switching the selected cell never needs
 * a new request — see `matchesSelection` below.
 *
 * Design: .claude/skills/siteoperations-design/SKILL.md (v2 Compact
 * Professional) — card/panel recipe, fieldCls inputs, primary/secondary
 * buttons, status-pill origin badges, empty state.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { MessageSquare, Plus, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { listNotes, createNote, type Note, type NoteOrigin } from '../../api/notes';

export interface NotesPanelSelection {
  /** The grid sheet name (plan §5.5 cta_sheets.name). */
  sheet: string;
  /** The cell reference as shown in the grid (e.g. "C12"). */
  cellRef: string;
  /** cta_cells.id, when the cell has been parsed into the database. */
  cellId?: number;
  /** cta_budget_lines.id, when the cell belongs to an interpreted budget line. */
  budgetLineId?: string;
}

export interface NotesPanelProps {
  negotiationId: string;
  /** Sent on every new note; does not narrow the list (negotiation-level notes, including outcome notes, must stay visible). */
  versionId?: string;
  /** The grid's current selection; omit to show only the negotiation's notes, uncollapsed. */
  selection?: NotesPanelSelection;
  /** Hides both add-forms; the list still renders. */
  readOnly?: boolean;
}

const ORIGIN_LABEL: Record<NoteOrigin, string> = {
  manual: 'Manual',
  outcome: 'Outcome',
  xlsx_comment: 'Excel comment',
  xlsx_thread: 'Excel thread',
  xlsx_cell_text: 'Excel cell text',
  docx_comment: 'Word comment',
};

const ORIGIN_PILL_CLS: Record<NoteOrigin, string> = {
  manual: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  outcome: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  xlsx_comment: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  xlsx_thread: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  xlsx_cell_text: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  docx_comment: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

const HARVESTED_ORIGINS: NoteOrigin[] = ['xlsx_comment', 'xlsx_thread', 'xlsx_cell_text', 'docx_comment'];

function matchesSelection(note: Note, selection: NotesPanelSelection): boolean {
  if (selection.cellId != null && note.cell_id === selection.cellId) return true;
  if (selection.budgetLineId && note.budget_line_id === selection.budgetLineId) return true;
  return note.provenance?.sheet === selection.sheet && note.provenance?.cell === selection.cellRef;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function provenanceTitle(note: Note): string {
  const p = note.provenance;
  const parts = [
    p?.file ? `file: ${p.file}` : null,
    p?.sheet ? `sheet: ${p.sheet}` : null,
    p?.cell ? `cell: ${p.cell}` : null,
    p?.section ? `section: ${p.section}` : null,
    p?.page != null ? `page: ${p.page}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'no document locator (typed note)';
}

function NoteItem({ note }: { note: Note }) {
  return (
    <li
      data-testid={`note-item-${note.id}`}
      className="rounded-lg border border-gray-200 p-2.5 text-sm dark:border-gray-700"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${ORIGIN_PILL_CLS[note.origin]}`}
        >
          {ORIGIN_LABEL[note.origin]}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{note.author_name || 'Unknown author'}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">&middot;</span>
        <span className="text-xs text-gray-400 dark:text-gray-500" title={provenanceTitle(note)}>
          {formatWhen(note.noted_at)}
        </span>
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">{note.body}</p>
    </li>
  );
}

export function NotesPanel({ negotiationId, versionId, selection, readOnly = false }: NotesPanelProps) {
  const [allNotes, setAllNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherExpanded, setOtherExpanded] = useState(!selection);

  const [draft, setDraft] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [outcomeDraft, setOutcomeDraft] = useState('');
  const [addingOutcome, setAddingOutcome] = useState(false);
  const [outcomeError, setOutcomeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!negotiationId) return;
    setLoading(true);
    setError(null);
    try {
      const notes = await listNotes({ negotiationId });
      setAllNotes(notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [negotiationId, versionId]);

  useEffect(() => {
    load();
  }, [load]);

  const { cellNotes, otherNotes } = useMemo(() => {
    if (!selection) return { cellNotes: [] as Note[], otherNotes: allNotes };
    const cell: Note[] = [];
    const other: Note[] = [];
    for (const note of allNotes) {
      (matchesSelection(note, selection) ? cell : other).push(note);
    }
    return { cellNotes: cell, otherNotes: other };
  }, [allNotes, selection]);

  async function handleAddNote(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setAddingNote(true);
    setAddError(null);
    try {
      const input: Parameters<typeof createNote>[0] = { negotiationId, versionId, body, origin: 'manual' };
      if (selection) {
        if (selection.cellId != null) {
          input.cellId = selection.cellId;
        } else if (selection.budgetLineId) {
          input.budgetLineId = selection.budgetLineId;
        } else {
          input.provenance = { sheet: selection.sheet, cell: selection.cellRef };
        }
      }
      const created = await createNote(input);
      setAllNotes((prev) => [created, ...prev]);
      setDraft('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  }

  async function handleAddOutcomeNote(e: FormEvent) {
    e.preventDefault();
    const body = outcomeDraft.trim();
    if (!body) return;
    setAddingOutcome(true);
    setOutcomeError(null);
    try {
      const created = await createNote({ negotiationId, body, origin: 'outcome' });
      setAllNotes((prev) => [created, ...prev]);
      setOutcomeDraft('');
    } catch (err) {
      setOutcomeError(err instanceof Error ? err.message : 'Failed to add outcome note');
    } finally {
      setAddingOutcome(false);
    }
  }

  const fieldCls =
    'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';
  const primaryBtnCls =
    'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0';
  const outlineBtnCls =
    'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';

  return (
    <div data-testid="notes-panel" className="flex flex-col gap-3 p-3">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && allNotes.length === 0 ? (
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Loading notes&hellip;
        </div>
      ) : (
        <>
          {selection && (
            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                <MessageSquare size={14} /> Notes on {selection.sheet}!{selection.cellRef}
              </h3>
              {cellNotes.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No notes on this cell yet.</p>
              ) : (
                <ul data-testid="notes-cell-list" className="flex flex-col gap-2">
                  {cellNotes.map((n) => (
                    <NoteItem key={n.id} note={n} />
                  ))}
                </ul>
              )}
            </section>
          )}

          <section>
            <button
              type="button"
              data-testid="notes-other-toggle"
              onClick={() => setOtherExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              {otherExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {selection ? 'Other notes on this negotiation' : 'Notes'} ({otherNotes.length})
            </button>
            {otherExpanded && (
              <ul data-testid="notes-other-list" className="mt-1.5 flex flex-col gap-2">
                {otherNotes.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No other notes yet.</p>
                ) : (
                  otherNotes.map((n) => <NoteItem key={n.id} note={n} />)
                )}
              </ul>
            )}
          </section>

          {!readOnly && (
            <form data-testid="notes-add-form" onSubmit={handleAddNote} className="flex flex-col gap-1.5">
              <label htmlFor="notes-add-input" className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {selection ? `Add a note on ${selection.sheet}!${selection.cellRef}` : 'Add a note'}
              </label>
              <textarea
                id="notes-add-input"
                data-testid="notes-add-input"
                className={fieldCls}
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a note&hellip;"
              />
              {addError && <p className="text-xs text-red-600 dark:text-red-400">{addError}</p>}
              <button
                type="submit"
                data-testid="notes-add-submit"
                disabled={addingNote || !draft.trim()}
                className={`${primaryBtnCls} self-start`}
              >
                <Plus size={16} /> Add note
              </button>
            </form>
          )}

          {!readOnly && (
            <form
              data-testid="notes-outcome-form"
              onSubmit={handleAddOutcomeNote}
              className="flex flex-col gap-1.5 border-t border-gray-200 pt-3 dark:border-gray-700"
            >
              <label htmlFor="notes-outcome-input" className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Outcome note (why the budget ended up as it did — required before this negotiation can be marked executed)
              </label>
              <textarea
                id="notes-outcome-input"
                data-testid="notes-outcome-input"
                className={fieldCls}
                rows={2}
                value={outcomeDraft}
                onChange={(e) => setOutcomeDraft(e.target.value)}
                placeholder='Type the outcome, or "reason not recorded" for a historical negotiation&hellip;'
              />
              {outcomeError && <p className="text-xs text-red-600 dark:text-red-400">{outcomeError}</p>}
              <button
                type="submit"
                data-testid="notes-outcome-submit"
                disabled={addingOutcome || !outcomeDraft.trim()}
                className={`${outlineBtnCls} self-start`}
              >
                <Plus size={16} /> Add outcome note
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

/** Exposed for a harvested-note check elsewhere (e.g. hiding an edit control on a harvested note). */
export function isHarvestedOrigin(origin: NoteOrigin): boolean {
  return HARVESTED_ORIGINS.includes(origin);
}

export default NotesPanel;
